import { useState } from "react";
import ChevronIcon from "../icons/ChevronIcon";
import CircularCheckmark from "../icons/CircularCheckmark";
import { useNavigate } from "react-router-dom";
import { Requirement } from "../../types/requirement.response";
import { AddableCourse, CourseLite } from "../../types/course.response";
import { ProgramSummaryResponse } from "../../types/program.response";
import { isRequirementFulfilled, getCompletedUnitsCount, getRequiredUnitsCount, getUnitsLabel, isOptionLikeRequirement, getRequirementRuleDescriptor } from "../../utils/requirementResponseUtils";
import { CourseRequirementOverride } from "../../services/api/user-courses.api";
import CompletedCoursesChips from "./HeaderCompletedCourses";
import VerificationWarning from "../shared/VerificationWarning";
import { useForceApplyAdd } from "./useForceApplyAdd";

interface ReqSectionHeaderProps {
    program: ProgramSummaryResponse;
    requirement: Requirement;
    handleAddCourse: (newCourse: AddableCourse, overrides: CourseRequirementOverride[], semester: string | null, classSectionIds?: number[]) => void | Promise<void>;
    handleDeleteCourse: (deletedCourse: CourseLite) => void;
    handleOverride: (course: CourseLite, override: CourseRequirementOverride) => void | Promise<void>;
    fromHome?: boolean;
    onRequirementClick?: (programId: string, requirementId: string) => void;
}

export default function ReqSectionHeader({ program, requirement, handleAddCourse, handleDeleteCourse, handleOverride, fromHome, onRequirementClick }: ReqSectionHeaderProps) {
    const navigate = useNavigate();
    const [showCompleted, setShowCompleted] = useState(false);

    const isFulfilled = isRequirementFulfilled(requirement);
    const completedCount = getCompletedUnitsCount(requirement);
    const requiredCount = getRequiredUnitsCount(requirement);
    const unitsLabel = getUnitsLabel(requirement);
    const ruleDescriptor = getRequirementRuleDescriptor(requirement, program.courses);

    const handleRequirementCourseAdd = useForceApplyAdd(requirement, handleAddCourse, handleOverride);

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-y-1">
                <div className="flex items-center gap-2 cursor-pointer hover:underline"
                        onClick={() => onRequirementClick
                            ? onRequirementClick(program.info.id, requirement.info.id)
                            : navigate(`/programs/${program.info.id}/requirements/${requirement.info.id}`, { state: { from: fromHome ? 'home' : 'program' } })}
                >
                    <h1 className="text-base md:text-lg font-semibold text-gray-900">{requirement.info.name}</h1>
                    {isFulfilled && (
                        <div className="flex items-center gap-0.5 md:gap-1 px-1.5 md:px-2 py-0.5 bg-green-50 border border-green-100 rounded-full">
                            <CircularCheckmark className="h-3 w-3 md:h-3.5 md:w-3.5 text-green-500" />
                            <span className="text-[9px] md:text-[10px] font-bold text-green-600 uppercase tracking-wide md:tracking-wider">Satisfied</span>
                        </div>
                    )}
                    <ChevronIcon direction="right" className="w-5 h-5" />
                </div>
                {isOptionLikeRequirement(requirement) ? (
                    requirement.info.needVerification && (
                        <span className="flex items-center text-xs md:text-sm font-medium text-gray-500">
                            <VerificationWarning />
                        </span>
                    )
                ) : (
                    <button
                        className="flex items-center gap-2 transition-colors text-xs md:text-sm font-medium text-gray-500 hover:text-gray-700"
                        onClick={() => setShowCompleted(!showCompleted)}
                    >
                        <span>{completedCount} / {requiredCount} {unitsLabel}</span>
                        {requirement.info.needVerification && <VerificationWarning />}
                        <ChevronIcon direction={showCompleted ? "up" : "down"} className="w-4 h-4" />
                    </button>
                )}
            </div>

            {ruleDescriptor && (
                <p className="mt-0.5 text-xs md:text-sm text-gray-400">{ruleDescriptor}</p>
            )}

            {showCompleted && !isOptionLikeRequirement(requirement) && (
                <CompletedCoursesChips
                    requirement={requirement}
                    coursesMap={program.courses}
                    onAdd={handleRequirementCourseAdd}
                    onDelete={handleDeleteCourse}
                    onOverride={handleOverride}
                />
            )}
        </div>
    );
}
